import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, KeyRound, Trash2, Pencil, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ROLE_STYLES = {
  admin: "bg-red-600 text-white",
  management: "bg-slate-900 text-orange-400",
  koordinator: "bg-emerald-100 text-emerald-800 border border-emerald-300",
};
const ROLE_LABEL = { admin: "Admin", management: "Management", koordinator: "Koordinator Lapangan" };

export default function UsersPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [users, setUsers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "koordinator" });
  const [editForm, setEditForm] = useState({ name: "", role: "koordinator" });
  const [newPass, setNewPass] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const submitNew = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`Akun ${form.email} berhasil dibuat`);
      setOpenNew(false);
      setForm({ email: "", password: "", name: "", role: "koordinator" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/users/${editUser.id}`, editForm);
      toast.success("Akun diperbarui");
      setEditUser(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/users/${resetUser.id}`, { password: newPass });
      toast.success(`Password ${resetUser.email} direset`);
      setResetUser(null);
      setNewPass("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const del = async (u) => {
    if (!confirm(`Hapus akun ${u.email}? Aksi ini tidak dapat dibatalkan.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Akun dihapus");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const canManage = (u) => {
    if (u.id === me?.id) return false;
    if (u.role === "admin") return false;
    if (isAdmin) return true;
    return u.role === "koordinator";
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900">Manajemen Akun</h1>
          <p className="text-slate-500 text-sm mt-1">
            {isAdmin
              ? "Sebagai Admin, Anda dapat mengelola semua akun (koordinator & management)."
              : "Sebagai Management, Anda hanya dapat mengelola akun Koordinator Lapangan."}
          </p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button
              className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase tracking-wider rounded-sm btn-industrial h-12"
              data-testid="add-user-btn"
            >
              <Plus className="w-5 h-5 mr-1" /> Tambah Akun
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm border-2 border-slate-900">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-black">Tambah Akun Baru</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitNew} className="space-y-4">
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Nama Lengkap *</Label>
                <Input data-testid="user-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 rounded-sm" placeholder="Budi Santoso" />
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Email *</Label>
                <Input data-testid="user-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 rounded-sm font-mono" placeholder="budi@perusahaan.id" />
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Password *</Label>
                <Input data-testid="user-password" required type="text" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-12 rounded-sm font-mono" placeholder="Minimal 6 karakter" />
                <p className="text-xs text-slate-500 mt-1">User bisa ganti password nanti. Untuk sekarang, informasikan password ini ke pemilik akun.</p>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Role *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="h-12 rounded-sm" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="koordinator">Koordinator Lapangan (input data, tidak lihat harga)</SelectItem>
                    {isAdmin && <SelectItem value="management">Management (lihat harga & laporan biaya)</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} data-testid="user-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
                  {busy ? "..." : "Buat Akun"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border-2 border-slate-200 bg-white rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-900 hover:bg-slate-900">
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Nama</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Email</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Role</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold w-52 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                <TableCell className="font-bold">{u.name} {u.id === me?.id && <span className="text-xs text-slate-500 font-normal">(Anda)</span>}</TableCell>
                <TableCell className="font-mono text-slate-700">{u.email}</TableCell>
                <TableCell>
                  <span className={`text-xs uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${ROLE_STYLES[u.role] || ""}`}>
                    {ROLE_LABEL[u.role] || u.role}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  {canManage(u) ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => { setResetUser(u); setNewPass(""); }} data-testid={`reset-${u.email}`} title="Reset password">
                        <KeyRound className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setEditUser(u); setEditForm({ name: u.name, role: u.role }); }} data-testid={`edit-user-${u.email}`} title="Edit">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => del(u)} data-testid={`delete-user-${u.email}`} title="Hapus">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </>
                  ) : (
                    <span className="text-xs text-slate-400 inline-flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" /> Tidak dapat diubah
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-12 text-slate-500">Belum ada akun.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit user dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="rounded-sm border-2 border-slate-900">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black">Edit Akun</DialogTitle></DialogHeader>
          {editUser && (
            <form onSubmit={submitEdit} className="space-y-4">
              <div className="border-2 border-slate-900 p-3 rounded-sm bg-slate-50">
                <div className="font-mono text-sm">{editUser.email}</div>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Nama</Label>
                <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="h-12 rounded-sm" data-testid="edit-name-input" />
              </div>
              {isAdmin && (
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Role</Label>
                  <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                    <SelectTrigger className="h-12 rounded-sm" data-testid="edit-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="koordinator">Koordinator Lapangan</SelectItem>
                      <SelectItem value="management">Management</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditUser(null)} className="rounded-sm">Batal</Button>
                <Button type="submit" disabled={busy} data-testid="edit-user-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
                  {busy ? "..." : "Simpan"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent className="rounded-sm border-2 border-slate-900">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black">Reset Password</DialogTitle></DialogHeader>
          {resetUser && (
            <form onSubmit={submitReset} className="space-y-4">
              <div className="border-2 border-slate-900 p-3 rounded-sm bg-slate-50">
                <div className="font-mono text-sm">{resetUser.email}</div>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Password Baru *</Label>
                <Input required type="text" minLength={6} value={newPass} onChange={(e) => setNewPass(e.target.value)} className="h-12 rounded-sm font-mono" data-testid="reset-password-input" placeholder="Minimal 6 karakter" />
                <p className="text-xs text-slate-500 mt-1">Informasikan password baru ini ke user secara langsung.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setResetUser(null)} className="rounded-sm">Batal</Button>
                <Button type="submit" disabled={busy || newPass.length < 6} data-testid="reset-password-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
                  {busy ? "..." : "Reset"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
